// AGC: v5.5.0 - Home page content component (separated from MainMenu app shell)
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { ChevronUp, ChevronDown, Sword, Swords } from 'lucide-react'
import { useAccount } from 'wagmi'
import { useContract } from '../hooks/useContract'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { usePvERefunds } from '../hooks/useP2PRefunds'
import { useClaimAirdropV2 } from '../hooks/useAirdropManagerV2'
import { useClaimAirdropV3, useCanClaimAirdropV3, getAirdropManagerV3Address } from '../hooks/useAirdropManagerV3'
import { MatchmakingLobby } from './MatchmakingLobby'
import { buildBackendUrl } from '../utils/backendUrl'
import { logger } from '../utils/logger'
import { playSoundSafe } from '../utils/audio'
import type { ActivePage } from './MainMenu'
import { useModal } from '../hooks/useModal'
import { usePlayerPreferences } from '../providers/PlayerPreferencesProvider'
import { TourWelcomeModal } from './modals/TourWelcomeModal'

// AGC: Home panel wrapper component
const HomePanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full max-w-sm rounded-xl bg-slate-900/80 backdrop-blur border border-slate-800 p-3 shadow-lg">
    {children}
  </div>
)

interface HomeProps {
  onNavigateToPage: (page: ActivePage) => void
  onShowGameGuide: () => void
  onOpenSuggestion?: () => void // AGC: Optional callback to open suggestion form
  onOpenEmergencyWithdrawStakingManager?: () => void // AGC: Open StakingManager emergency withdraw
  onOpenEmergencyWithdrawStakingRevenueShare?: () => void // AGC: Open StakingRevenueShare emergency withdraw
}

// AGC: Battle history types - matches backend API response structure
interface BattleParticipant {
  address: string | null
  level: number | null
  isBot: boolean
  xp: number | null
  energy: number | null
}

interface BattleRecord {
  battleId: string
  battleType: string
  mode: string | null
  isDraw: boolean
  createdAt: number | null
  endedAt: number | null
  timestamp: number | null
  turns: number | null
  battleLogLength: number | null
  winnerAddress: string | null
  loserAddress: string | null
  resolutionTxHash: string | null
  creationTxHash: string | null
  player1XP: number | null
  player2XP: number | null
  winnerXP: number | null
  loserXP: number | null
  drawXP: number | null
  difficulty: string | null
  player1: BattleParticipant | null
  player2: BattleParticipant | null
  source?: string
  // AGC: Legacy fields for backward compatibility (may be undefined)
  playerMove?: string
  aiMove?: string
  playerAddress?: string
  aiLevel?: string
  outcome?: string
  playerXPGained?: number
  turnCount?: number
}

const RECENT_BATTLE_LIMIT = 10

// AGC: Multi-Place states: claim → play
type MultiPlaceState = 'claim-airdrop' | 'claim-refund' | 'play-buttons'

export const Home: React.FC<HomeProps> = ({ onNavigateToPage, onShowGameGuide, onOpenSuggestion, onOpenEmergencyWithdrawStakingManager, onOpenEmergencyWithdrawStakingRevenueShare }) => {
  const { address } = useAccount()
  const { getTotalBurned } = useContract()
  const { canClaim } = usePlayerStatus(address)
  const { refundableBattles, hasRefunds, claimRefund, refresh: refreshRefunds } = usePvERefunds(address || undefined)
  const { preferences } = usePlayerPreferences()
  
  // AGC: AirdropManager hooks - V3 takes priority if configured
  const airdropV3Address = getAirdropManagerV3Address()
  const v3Hook = useClaimAirdropV3()
  const v2Hook = useClaimAirdropV2()
  
  // AGC: Check V3 claim status (tokens claimed, NFTs not claimed)
  const { canClaim: canClaimV3Full, canClaimNFTsOnly, hasClaimedTokens, hasClaimedNFTs } = useCanClaimAirdropV3(address)
  
  // AGC: Use V3 if available, otherwise fallback to V2
  const claimHook = airdropV3Address ? v3Hook : v2Hook
  const { claimTokenAirdrop, claimNFTAirdrop, claimNFTsOnly, claimFullAirdrop, isPending: isClaimingAirdrop, isConfirming: isConfirmingAirdrop, error: airdropError } = claimHook
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()
  
  // AGC: Tour welcome modal state (v6.9.0)
  const [showTourWelcome, setShowTourWelcome] = useState(false)
  const [hasCheckedTour, setHasCheckedTour] = useState(false)
  
  // AGC: State for total burned tokens
  const [totalBurned, setTotalBurned] = useState<string>('0')

  const [isClaiming, setIsClaiming] = useState(false)
  const [claimStep, setClaimStep] = useState<'idle' | 'tokens' | 'nfts' | 'success'>('idle')

  // AGC: Battle history state
  const [recentBattles, setRecentBattles] = useState<BattleRecord[]>([])
  const [recentBattlesLoading, setRecentBattlesLoading] = useState(true)
  const [recentBattlesError, setRecentBattlesError] = useState<string | null>(null)
  const [isBattlePanelExpanded, setIsBattlePanelExpanded] = useState(false)

  // AGC: Suggestion button state
  const [isSuggestionExpanded, setIsSuggestionExpanded] = useState(false)
  const [isSuggestionPeek, setIsSuggestionPeek] = useState(false) // AGC: Track if button is in peek mode (25% visible)
  const [isContentLoaded, setIsContentLoaded] = useState(false) // AGC: Track when content is loaded
  const suggestionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const suggestionExpandTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // AGC: Determine Multi-Place state dynamically (always reflects latest claim/refund status)
  // AGC: When V3 is enabled, use V3's claim status (hasClaimedTokens) - it's authoritative
  // AGC: canClaimNFTsOnly = true when tokens are claimed but NFTs are not (V3 only)
  const multiPlaceState: MultiPlaceState = useMemo(() => {
    // AGC: When V3 is configured, use V3's claim status (more accurate than V2)
    if (airdropV3Address) {
      // AGC: Can claim full airdrop if V3 says so (tokens + NFTs not claimed)
      if (address && canClaimV3Full) return 'claim-airdrop'
      // AGC: Can claim NFTs only (tokens already claimed but NFTs not)
      if (address && canClaimNFTsOnly) return 'claim-airdrop'
      // AGC: Already claimed both - show refund or play buttons
      if (address && hasRefunds) return 'claim-refund'
      return 'play-buttons'
    }
    
    // AGC: Fallback to V2 logic when V3 is not configured
    if (address && canClaim) return 'claim-airdrop'
    if (address && hasRefunds) return 'claim-refund'
    return 'play-buttons'
  }, [address, canClaim, canClaimV3Full, canClaimNFTsOnly, hasRefunds, airdropV3Address])

  // AGC: Handle airdrop claim (tokens + NFTs)
  const handleClaimAirdrop = async () => {
    setIsClaiming(true)
    setClaimStep('tokens')
    try {
      // AGC: Use claimFullAirdrop which works for both V2 and V3
      console.log('🎁 AGC: Claiming full airdrop...', airdropV3Address ? '(V3)' : '(V2)')
      const result = await claimFullAirdrop()
      logger.info('Airdrop claimed successfully!', result)
        
      // AGC: V3 returns { tokenTxHash, tokenAmount, tokenReason, nftTxHash }
      // AGC: V2 returns { tokenTxHash, nftTxHash }
      const amountMessage = result.tokenAmount 
        ? `You received ${result.tokenAmount} OGB ${result.tokenReason ? `(${result.tokenReason})` : ''}`
        : 'Tokens staked with lockup'
        
      setClaimStep('success')
      await alert(`✅ Airdrop claimed successfully! ${amountMessage} and NFTs minted.`)
    } catch (error: any) {
      logger.error('Airdrop claim failed:', error)
      // AGC: Extract error message - could be from backend or contract
      let errorMessage = 'Failed to claim airdrop'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.reason) {
        errorMessage = error.reason
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      await alert(`❌ ${errorMessage}`)
    } finally {
      setIsClaiming(false)
      setClaimStep('idle')
    }
  }

  // AGC: Handle NFT-only claim (for users who already claimed tokens but missed NFTs)
  const handleClaimNFTsOnly = async () => {
    setIsClaiming(true)
    setClaimStep('nfts')
    try {
      console.log('🎁 AGC: Claiming NFTs only (V3)...')
      const result = await claimNFTsOnly()
      logger.info('NFT airdrop claimed successfully!', result)
        
      setClaimStep('success')
      await alert('✅ NFTs claimed successfully! Check your inventory for Buffs and Potions.')
    } catch (error: any) {
      logger.error('NFT claim failed:', error)
      let errorMessage = 'Failed to claim NFTs'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.reason) {
        errorMessage = error.reason
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      await alert(`❌ ${errorMessage}`)
    } finally {
      setIsClaiming(false)
      setClaimStep('idle')
    }
  }

  // AGC: Handle refund claim
  const handleClaimRefund = async () => {
    if (refundableBattles.length === 0) return
    
    setIsClaiming(true)
    try {
      const battleToRefund = refundableBattles[0]
      console.log('💰 [Home] Claiming refund for battle:', battleToRefund.battleId)
      const txHash = await claimRefund(battleToRefund.battleId)
      console.log('✅ [Home] Refund transaction submitted:', txHash)
      logger.info('Refund claimed successfully!')
      
      // AGC: Force refresh refundable battles list to update UI
      // The hook should already do this, but we'll wait a bit for the transaction to be mined
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // AGC: Explicitly refresh to update hasRefunds state
      await refreshRefunds()
      
    } catch (error: any) {
      console.error('❌ [Home] Refund claim failed:', error)
      logger.error('Refund claim failed:', error)
      
      // AGC: Show user-friendly error message
      const errorMessage = error?.message || error?.reason || 'Unknown error'
      await alert(`Refund failed: ${errorMessage}`, { type: 'error' })
    } finally {
      setIsClaiming(false)
    }
  }

  // AGC: Show tour welcome modal on first visit (v6.9.0)
  useEffect(() => {
    if (hasCheckedTour) return
    
    // AGC: Small delay to ensure preferences are loaded
    const timer = setTimeout(() => {
      setHasCheckedTour(true)
      
      // AGC: Show modal if preference is enabled (user can enable it in settings regardless of completion status)
      if (preferences.showTourWelcomeModal) {
        logger.info('🎮 AGC: Showing tour welcome modal (enabled in settings)')
        setShowTourWelcome(true)
      }
    }, 500) // AGC: Short delay to let page render first
    
    return () => clearTimeout(timer)
  }, [hasCheckedTour, preferences.showTourWelcomeModal])


  // AGC: Load total burned tokens on mount
  useEffect(() => {
    const loadBurnedTokens = async () => {
      try {
        const burned = await getTotalBurned()
        setTotalBurned(burned)
      } catch (error: any) {
        // AGC: Silently ignore CALL_EXCEPTION errors for burn address (contract might not support balanceOf for 0xdead)
        if (error?.code === 'CALL_EXCEPTION' || error?.code === 'UNPREDICTABLE_GAS_LIMIT') {
          // AGC: This is expected - burn address might not be queryable, set to 0
          setTotalBurned(0)
          return
        }
        // AGC: Only log unexpected errors
        logger.error('Failed to load burned tokens:', error)
      }
    }
    loadBurnedTokens()
  }, [getTotalBurned])

  // AGC: Load recent battles from backend
  useEffect(() => {
    const loadRecentBattles = async () => {
      try {
        setRecentBattlesLoading(true)
        setRecentBattlesError(null)

        const backendUrl = buildBackendUrl('api/battles/recent')
        logger.debug(`Loading recent battles from: ${backendUrl}`)

        // AGC: CRITICAL - Simple fetch without AbortController for Base App compatibility
        const response = await fetch(backendUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        logger.debug(`Recent battles response status: ${response.status} ${response.statusText}`)

        if (!response.ok) {
          const errorText = await response.text()
          logger.error(`Backend returned error: ${response.status}`, errorText)
          throw new Error(`Backend error (${response.status}): ${errorText || 'Unknown error'}`)
        }

        const data = await response.json()
        logger.debug(`Recent battles response data:`, { 
          battlesCount: data.battles?.length || 0, 
          source: data.source 
        })

        // AGC: Backend returns { battles: [...], source: 'firestore' | 'memory' } (no success field)
        if (Array.isArray(data.battles)) {
          setRecentBattles(data.battles.slice(0, RECENT_BATTLE_LIMIT))
          logger.info(`Loaded ${data.battles.length} recent battles from ${data.source}`)
        } else {
          logger.warn('Backend returned invalid battles array')
          setRecentBattles([])
        }
      } catch (error: any) {
        logger.error('Failed to load recent battles:', error)
        setRecentBattlesError(error.message || 'Unable to load battle feed')
        setRecentBattles([])
      } finally {
        setRecentBattlesLoading(false)
        // AGC: Mark content as loaded when battles finish loading
        setIsContentLoaded(true)
      }
    }

    loadRecentBattles()
  }, [])

  // AGC: Suggestion button auto-hide logic - hide to peek mode after 5 seconds
  useEffect(() => {
    if (!isContentLoaded) return

    // AGC: After 5 seconds, hide button to peek mode (25% visible on right)
    const hideTimer = setTimeout(() => {
      setIsSuggestionPeek(true)
    }, 5000) // AGC: Hide to peek after 5 seconds

    return () => {
      clearTimeout(hideTimer)
    }
  }, [isContentLoaded])

  // AGC: Handle suggestion button click - expand when in peek mode, open form when expanded
  const handleSuggestionClick = () => {
    playSoundSafe('/sounds/click.mp3', 0.3)
    
    // AGC: Clear any existing timeout
    if (suggestionExpandTimeoutRef.current) {
      clearTimeout(suggestionExpandTimeoutRef.current)
    }
    
    // AGC: If in peek mode, expand to show full button with "Send suggestions" text
    if (isSuggestionPeek && !isSuggestionExpanded) {
      setIsSuggestionExpanded(true)
      setIsSuggestionPeek(false) // AGC: Exit peek mode when expanded
      
      // AGC: Auto-collapse back to peek mode after 5 seconds if not clicked
      suggestionExpandTimeoutRef.current = setTimeout(() => {
        setIsSuggestionExpanded(false)
        setIsSuggestionPeek(true) // AGC: Return to peek mode
      }, 5000)
    } else if (isSuggestionExpanded) {
      // AGC: If already expanded, open suggestion form
      handleOpenSuggestion()
    }
  }

  // AGC: Handle opening suggestion form
  const handleOpenSuggestion = () => {
    // AGC: Clear timeout
    if (suggestionExpandTimeoutRef.current) {
      clearTimeout(suggestionExpandTimeoutRef.current)
    }
    
    // AGC: Navigate to suggestion form
    if (onOpenSuggestion) {
      onOpenSuggestion()
    } else {
      onNavigateToPage('suggestionForm')
    }
  }

  // AGC: Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current)
      }
      if (suggestionExpandTimeoutRef.current) {
        clearTimeout(suggestionExpandTimeoutRef.current)
      }
    }
  }, [])

  // AGC: Battle history helpers
  const hasBattles = recentBattles.length > 0
  const latestBattle = recentBattles[0]

  const getOutcomeColor = (battle: BattleRecord) => {
    // AGC: Use new structure (isDraw/winnerAddress) if available
    if (battle.isDraw) return 'text-yellow-400'
    if (battle.winnerAddress && address && battle.winnerAddress.toLowerCase() === address.toLowerCase()) {
      return 'text-green-400'
    }
    if (battle.winnerAddress && address && battle.winnerAddress.toLowerCase() !== address.toLowerCase()) {
      return 'text-red-400'
    }
    // AGC: Fallback to legacy outcome field
    if (battle.outcome === 'win') return 'text-green-400'
    if (battle.outcome === 'loss') return 'text-red-400'
    return 'text-yellow-400'
  }

  const formatBattleOutcomeLabel = (battle: BattleRecord) => {
    // AGC: Use new structure (isDraw/winnerAddress) if available
    if (battle.isDraw) return '⚖️ Draw'
    if (battle.winnerAddress && address && battle.winnerAddress.toLowerCase() === address.toLowerCase()) {
      return '✅ Victory'
    }
    if (battle.winnerAddress && address && battle.winnerAddress.toLowerCase() !== address.toLowerCase()) {
      return '❌ Defeat'
    }
    // AGC: Fallback to legacy outcome field
    if (battle.outcome === 'win') return '✅ Victory'
    if (battle.outcome === 'loss') return '❌ Defeat'
    return '⚖️ Draw'
  }

  const formatBattleTypeLabel = (battle: BattleRecord) => {
    // AGC: Use new battleType field (uppercase)
    if (battle.battleType?.toUpperCase() === 'PVP') return 'PvP'
    if (battle.battleType?.toUpperCase() === 'PVE') {
      const difficulty = battle.difficulty || 'Normal'
      return `PvE (${difficulty})`
    }
    // AGC: Fallback to legacy logic
    if (battle.battleType === 'p2p') return 'PvP'
    return `PvE (${battle.aiLevel || 'Normal'})`
  }

  // AGC: Format matchup showing player1 vs player2 (or BOT only for actual bot address)
  const formatMatchup = (battle: BattleRecord) => {
    // AGC: Get bot address from environment
    const botAddress = (import.meta.env.VITE_BATTLE_BOT_ADDRESS || '0x88e1e984881CBd756a5d6B398B64d6493047b208').toLowerCase()
    
    // AGC: Use new structure (player1/player2) if available
    if (battle.player1 && battle.player2) {
      const formatParticipant = (p: BattleParticipant | null) => {
        if (!p) return 'Player'
        // AGC: Only show "BOT" if address exactly matches VITE_BATTLE_BOT_ADDRESS (not PlayerAI)
        if (p.address && p.address.toLowerCase() === botAddress) {
          return `BOT (Lvl ${p.level ?? '?'})`
        }
        // AGC: Treat PlayerAI and regular players the same - always show address if available
        if (p.address) {
          // AGC: Format address as 0x8d9...51aD (first 5 chars + last 4 chars)
          const addr = p.address.slice(0, 5) + '...' + p.address.slice(-4)
          return p.level ? `Lvl ${p.level} (${addr})` : addr
        }
        // AGC: Fallback: show level if no address (shouldn't happen but handle gracefully)
        return `Lvl ${p.level ?? '?'}`
      }
      return `${formatParticipant(battle.player1)} vs ${formatParticipant(battle.player2)}`
    }
    
    // AGC: Fallback to legacy fields if new structure not available
    const playerMove = battle.playerMove || '?'
    const aiMove = battle.aiMove || '?'
    return `${playerMove} vs ${aiMove}`
  }

  const formatTurnsSummary = (battle: BattleRecord) => {
    // AGC: Use new turns field if available, fallback to battleLogLength or turnCount
    const turns = battle.turns ?? battle.battleLogLength ?? battle.turnCount ?? 1
    return `${turns} turn${turns !== 1 ? 's' : ''}`
  }

  const formatXpSummary = (battle: BattleRecord) => {
    // AGC: Use new XP fields (winnerXP/loserXP/drawXP) if available
    if (battle.isDraw && battle.drawXP !== null) {
      return `+${battle.drawXP} XP (Draw)`
    }
    if (battle.winnerXP !== null && address && battle.winnerAddress?.toLowerCase() === address.toLowerCase()) {
      return `+${battle.winnerXP} XP`
    }
    if (battle.loserXP !== null && address && battle.loserAddress?.toLowerCase() === address.toLowerCase()) {
      return `+${battle.loserXP} XP`
    }
    // AGC: Fallback to legacy playerXPGained field
    const xp = battle.playerXPGained ?? 0
    return `+${xp} XP`
  }

  const formatBattleTimestamp = (timestamp?: string | number | null) => {
    // AGC: Handle both string and number timestamps
    if (!timestamp) return 'Unknown time'
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <main className="pt-[72px] pb-[88px] flex flex-col items-center gap-3 px-3 text-white relative min-h-[calc(100vh-160px)]">
      {/* AGC: v5.5.0 - Multi-Place Area (claim buttons OR play buttons) */}
      <div className="w-full max-w-sm my-4">
            
            {/* AGC: State 1 - Claim Airdrop (Full or NFT-only) */}
            {/* AGC: Added data-tour="battle-entry" so navigation tour step 14 works for first-time players */}
            {multiPlaceState === 'claim-airdrop' && (
              <div data-tour="battle-entry" className="flex flex-col items-center gap-4 animate-in fade-in duration-500">
                <div className="text-center">
                  {canClaimNFTsOnly ? (
                    <>
                      <h3 className="text-xl font-bold text-purple-400 mb-1">🎁 NFTs Available!</h3>
                      <p className="text-sm text-slate-300">Tokens claimed ✓ — Claim your NFTs now</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-yellow-400 mb-1">🎁 Airdrop Available!</h3>
                      <p className="text-sm text-slate-300">Claim your free OGB tokens</p>
                    </>
                  )}
                </div>
                <button
                  data-tour="airdrop-button"
                  onClick={canClaimNFTsOnly ? handleClaimNFTsOnly : handleClaimAirdrop}
                  disabled={isClaiming || isClaimingAirdrop || isConfirmingAirdrop}
                  className={`w-full ${canClaimNFTsOnly 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                    : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                  } disabled:from-slate-600 disabled:to-slate-700 text-white font-bold py-3 px-6 rounded-lg transition-all active:scale-95 shadow-lg`}
                >
                  {isClaiming || isClaimingAirdrop || isConfirmingAirdrop
                    ? (claimStep === 'tokens' ? 'Claiming Tokens...' : claimStep === 'nfts' ? 'Claiming NFTs...' : 'Claiming...')
                    : canClaimNFTsOnly ? 'Claim NFTs (Buffs & Potions)' : 'Claim Airdrop (Tokens + NFTs)'}
                </button>
              </div>
            )}

            {/* AGC: State 2 - Claim Refund */}
            {/* AGC: Added data-tour="battle-entry" so navigation tour step 14 works for players with refunds */}
            {multiPlaceState === 'claim-refund' && (
              <div data-tour="battle-entry" className="flex flex-col items-center gap-4 animate-in fade-in duration-500">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-green-400 mb-1">💰 Refund Available!</h3>
                  <p className="text-sm text-slate-300">
                    {refundableBattles.length} battle{refundableBattles.length > 1 ? 's' : ''} pending refund
                  </p>
                </div>
                <button
                  onClick={handleClaimRefund}
                  disabled={isClaiming}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-slate-600 disabled:to-slate-700 text-white font-bold py-3 px-6 rounded-lg transition-all active:scale-95 shadow-lg"
                >
                  {isClaiming ? 'Claiming...' : 'Claim Refund'}
                </button>
              </div>
            )}

            {/* AGC: State 3 - Play Buttons (Premium game-style with gradients, glow, shadows, icons) */}
            {multiPlaceState === 'play-buttons' && (
              <div data-tour="battle-entry" className="flex w-full overflow-hidden rounded-lg animate-in fade-in duration-500" style={{ gap: 0 }}>
                {/* Left: PvE Button - Blue gradient with glow */}
                <button
                  data-tour="play-pve"
                  onClick={() => onNavigateToPage('quickBattle')}
                  className="flex-1 text-white font-bold py-2.5 px-4 transition-all active:scale-[0.98] relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(to bottom, #00C4FF, #0078FF)',
                    boxShadow: '0 4px 20px rgba(0, 196, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.2)',
                    border: 'none',
                    position: 'relative'
                  }}
                >
                  {/* AGC: Subtle noise texture overlay */}
                  <div 
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                      pointerEvents: 'none'
                    }}
                  />
                  {/* AGC: Top highlight */}
                  <div className="absolute top-0 left-0 right-0 h-[30%] bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
                  
                  <div className="flex flex-col items-center gap-0.5 relative z-10">
                    <div className="flex items-center gap-1.5">
                      <Sword className="w-4 h-4" />
                      <span className="text-base font-bold">PLAY PvE</span>
                    </div>
                    <span className="text-[10px] opacity-80">Quick Bot Battle</span>
                  </div>
                </button>
                
                {/* Right: PvP Button - Green gradient with glow */}
                <button
                  data-tour="play-pvp"
                  onClick={() => onNavigateToPage('p2pBattle')}
                  className="flex-1 text-white font-bold py-2.5 px-4 transition-all active:scale-[0.98] relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(to bottom, #00D16C, #009950)',
                    boxShadow: '0 4px 20px rgba(0, 209, 108, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.2)',
                    border: 'none',
                    position: 'relative'
                  }}
                >
                  {/* AGC: Subtle noise texture overlay */}
                  <div 
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                      pointerEvents: 'none'
                    }}
                  />
                  {/* AGC: Top highlight */}
                  <div className="absolute top-0 left-0 right-0 h-[30%] bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
                  
                  <div className="flex flex-col items-center gap-0.5 relative z-10">
                    <div className="flex items-center gap-1.5">
                      <Swords className="w-4 h-4" />
                      <span className="text-base font-bold">PLAY PvP</span>
                    </div>
                    <span className="text-[10px] opacity-80">Find Match</span>
                  </div>
                </button>
              </div>
            )}
          </div>

      {/* Total Tokens Burned */}
      <HomePanel>
        <div data-tour="tokens-burned" className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase text-slate-400 tracking-wide">Total Tokens Burned</p>
            <p className="text-xl font-bold text-white">{totalBurned} OGB</p>
            <p className="text-[10px] text-slate-500">Deflationary economy across Base</p>
          </div>
          <div className="rounded-full bg-rose-500/20 text-2xl px-3 py-2">🔥</div>
        </div>
      </HomePanel>

      {/* Matchmaking Queue */}
      <div data-tour="matchmaking-queue" className="w-full max-w-sm">
        <MatchmakingLobby
          title="Matchmaking Queue"
          className="bg-slate-900/80 border-slate-800 rounded-xl p-3 shadow-inner shadow-black/20"
          onJoinMatch={(targetAddress, difficulty, level) => {
            // AGC: Store join target in sessionStorage and navigate to P2P page
            sessionStorage.setItem('p2pJoinTarget', JSON.stringify({
              targetAddress,
              difficulty,
              level,
              timestamp: Date.now()
            }))
            onNavigateToPage('p2pBattle')
          }}
        />
      </div>

      {/* Recent Battles */}
      <HomePanel>
        <div data-tour="recent-battles" className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase text-slate-400 tracking-wide">Recent Battles</p>
            <p className="text-base font-semibold text-white">
              {hasBattles
                ? `Last ${Math.min(RECENT_BATTLE_LIMIT, recentBattles.length)} Battles`
                : recentBattlesLoading
                  ? 'Loading battles...'
                  : 'No battles recorded'}
            </p>
            {recentBattlesError && <p className="text-[10px] text-red-400">{recentBattlesError}</p>}
          </div>
          <button
            onClick={() => setIsBattlePanelExpanded((prev) => !prev)}
            disabled={!hasBattles}
            className={`rounded-full p-2 border ${
              hasBattles ? 'border-slate-700 text-slate-200 hover:bg-slate-700' : 'border-slate-800 text-slate-600 cursor-not-allowed'
            }`}
            aria-label={isBattlePanelExpanded ? 'Collapse battles' : 'Expand battles'}
          >
            {isBattlePanelExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        {recentBattlesLoading && !hasBattles ? (
          <p className="text-[11px] text-slate-400">Fetching global battle history...</p>
        ) : hasBattles ? (
          isBattlePanelExpanded ? (
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-800 mt-2">
              {recentBattles.map((battle) => (
                <div key={battle.battleId} className="py-2 flex items-start justify-between gap-3 text-xs">
                  <div>
                    <div className={`font-semibold ${getOutcomeColor(battle)}`}>
                      {formatBattleOutcomeLabel(battle)} • {formatBattleTypeLabel(battle)}
                    </div>
                    <div className="text-slate-400">
                      {formatMatchup(battle)} • {formatTurnsSummary(battle)} • {formatXpSummary(battle)}
                    </div>
                  </div>
                  <span className="text-slate-500 whitespace-nowrap">
                    {formatBattleTimestamp(battle.timestamp ?? battle.endedAt ?? battle.createdAt ?? undefined)}
                  </span>
                </div>
              ))}
            </div>
          ) : latestBattle ? (
            <div className="text-xs text-slate-300 space-y-1">
              <div className={`font-semibold ${getOutcomeColor(latestBattle)}`}>
                {formatBattleOutcomeLabel(latestBattle)} • {formatBattleTypeLabel(latestBattle)}
              </div>
              <div className="text-slate-400">
                {formatMatchup(latestBattle)} • {formatTurnsSummary(latestBattle)} • {formatXpSummary(latestBattle)}
              </div>
              <div className="text-slate-500">
                {formatBattleTimestamp(latestBattle.timestamp ?? latestBattle.endedAt ?? latestBattle.createdAt ?? undefined)}
              </div>
            </div>
          ) : null
        ) : (
          <p className="text-[11px] text-slate-400">Global battle feed will appear once matches complete.</p>
        )}
      </HomePanel>

      {/* Game Guide */}
      <HomePanel>
        <div data-tour="game-guide" className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase text-slate-400 tracking-wide">Guide</p>
            <p className="text-base font-semibold text-white">How to Play</p>
          </div>
          <button
            onClick={onShowGameGuide}
            className="flex items-center gap-1 rounded-lg bg-blue-600/80 px-3 py-1.5 text-xs font-semibold shadow-lg shadow-blue-500/30 border border-blue-400/30 hover:bg-blue-700"
          >
            Open
          </button>
        </div>
        <ul className="text-[11px] text-slate-300 space-y-1">
          <li>• Choose your origami hand (Rock, Paper, Scissors)</li>
          <li>• Pick an element advantage to tilt the odds</li>
          <li>• Equip buffs for extra damage or defense</li>
        </ul>
      </HomePanel>

      {/* AGC: Whitepaper Panel */}
      <HomePanel>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase text-slate-400 tracking-wide">Documentation</p>
            <p className="text-base font-semibold text-white">Whitepaper</p>
          </div>
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onNavigateToPage('whitepaper')
            }}
            className="flex items-center gap-1 rounded-lg bg-purple-600/80 px-3 py-1.5 text-xs font-semibold shadow-lg shadow-purple-500/30 border border-purple-400/30 hover:bg-purple-700 transition-colors"
          >
            Read
          </button>
        </div>
        <p className="text-[11px] text-slate-300 mt-2">
          Technical documentation for blockchain investors and developers.
        </p>
      </HomePanel>

      {/* AGC: Floating Suggestion Button - Bottom Right */}
      <div
        className="fixed bottom-24 right-4 z-30 transition-all duration-300"
        style={{
          transform: isSuggestionPeek 
            ? 'translateX(calc(75%))' // AGC: Hide to right, leaving 25% visible (75% of button width hidden)
            : 'translateX(0)', // AGC: Fully visible when not in peek mode
          opacity: 1, // AGC: Always fully opaque
        }}
      >
        <button
          onClick={handleSuggestionClick}
          className={`flex items-center gap-2 rounded-full shadow-lg transition-all duration-300 ${
            isSuggestionExpanded
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-white font-semibold text-sm'
              : 'bg-gradient-to-r from-blue-600 to-purple-600 w-14 h-14 items-center justify-center'
          } hover:scale-105 active:scale-95`}
          style={{
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.5)',
          }}
        >
          <img
            src={`${import.meta.env.BASE_URL}suggestionbox.Png`}
            alt="Suggestions"
            className="w-6 h-6 object-contain"
          />
          {isSuggestionExpanded && (
            <span className="whitespace-nowrap animate-in fade-in slide-in-from-right duration-200">
              Send suggestions
            </span>
          )}
        </button>
      </div>

      {/* AGC: Modal components */}
      {Modals}
      
      {/* AGC: Tour Welcome Modal (v6.9.0) */}
      <TourWelcomeModal
        isOpen={showTourWelcome}
        onClose={() => setShowTourWelcome(false)}
        onStartSimulation={() => onNavigateToPage('newSimulation')}
      />
    </main>
  )
}

